/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  AN OFFICER'S TRAIL — their last fifteen addresses and last hundred locations. [ANALYTICS]
 *
 *  ⚠️ A MODULE WITH NO IMPORTS: `pwa/` reads the send interval and the entry cap at RUNTIME
 *  (the throttle and the box's copy; the state and outcome lists reach it as types only),
 *  and CLAUDE.md §8 permits it a runtime import from `@pams/shared/constants` and from nowhere
 *  else — every other barrel drags zod in.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * How many distinct ADDRESSES an officer's record keeps. The cap is applied by the write itself,
 * so a sixteenth address evicts the oldest in the same update.
 *
 * ⚠️ [amended LOCATION-ANYTIME] ADDRESSES ONLY. It covered locations too until the location cap
 * below was raised on its own.
 */
export const OFFICER_TRAIL_MAX_ENTRIES = 15;

/**
 * [LOCATION-ANYTIME] How many LOCATIONS an officer's record keeps — the user's decision (2026-10-13),
 * raised from fifteen once the app sends on every open. Applied by the write itself, so the hundred
 * and first location evicts the oldest in the same update. About 10 KB of an officer document when full.
 */
export const OFFICER_LOCATION_MAX_ENTRIES = 100;

/**
 * Fifteen minutes — Duty Mitra's own interval, and the one `GET /app/me/location/status` advises.
 *
 * ⚠️ [amended LOCATION-ANYTIME] THE SERVER NO LONGER ENFORCES IT. `POST /app/me/location` records
 * every send, on duty or not, with no interval — the user's decision. Duty Mitra still asks the
 * status first and throttles itself to this value, which is what keeps its consent box true.
 */
export const LOCATION_SHARE_MIN_INTERVAL_MS = 15 * 60 * 1000;

/**
 * What `GET /app/me/location/status` answers: whether a client that shares only while a duty is in
 * progress, at most once per `LOCATION_SHARE_MIN_INTERVAL_MS` — Duty Mitra — should send now.
 * [amended LOCATION-ANYTIME] Advice since the POST stopped refusing; it no longer predicts a refusal.
 */
export const LOCATION_SHARE_STATES = ['DUE', 'NO_CURRENT_DUTY', 'TOO_SOON'] as const;
export type LocationShareState = (typeof LOCATION_SHARE_STATES)[number];

/**
 * What `POST /app/me/location` answers, always a 200.
 *
 * ⚠️ [amended LOCATION-ANYTIME] THE SERVER ANSWERS `RECORDED` AND NOTHING ELSE NOW. `NO_CURRENT_DUTY`
 * and `TOO_SOON` stay in the list so an app built against the old contract still parses the answer.
 */
export const LOCATION_SHARE_OUTCOMES = ['RECORDED', 'NO_CURRENT_DUTY', 'TOO_SOON'] as const;
export type LocationShareOutcome = (typeof LOCATION_SHARE_OUTCOMES)[number];
